import { useEffect, useState } from 'react';
import './PixelArtOffice.css';

interface AgentState {
  id: string;
  role: 'coordinator' | 'executor' | 'observer';
  name: string;
  status: 'active' | 'busy' | 'idle';
  currentTask?: string;
}

interface TerminalMessage {
  id: string;
  agent: string;
  message: string;
  timestamp: number;
}

interface PixelArtOfficeProps {
  agents?: AgentState[];
  terminalMessages?: TerminalMessage[];
  showConnections?: boolean;
}

const defaultAgents: AgentState[] = [
  { id: '1', role: 'coordinator', name: 'Peter', status: 'active' },
  { id: '2', role: 'executor', name: 'James', status: 'idle' },
  { id: '3', role: 'observer', name: 'John', status: 'active' },
];

export function PixelArtOffice({ 
  agents = defaultAgents, 
  terminalMessages = [],
  showConnections = true 
}: PixelArtOfficeProps) {
  const [particles, setParticles] = useState<number[]>([]);
  const [visibleMessages, setVisibleMessages] = useState<TerminalMessage[]>([]);

  // Generate floating particles
  useEffect(() => {
    const interval = setInterval(() => {
      setParticles(prev => [...prev, Date.now()].slice(-10));
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Update visible terminal messages
  useEffect(() => {
    setVisibleMessages(terminalMessages.slice(-4));
  }, [terminalMessages]);

  const getAgentClass = (role: string) => {
    switch (role) {
      case 'coordinator': return 'agent-coordinator';
      case 'executor': return 'agent-executor';
      case 'observer': return 'agent-observer';
      default: return 'agent-coordinator';
    }
  };

  const getStatusClass = (status: string) => {
    switch (status) {
      case 'active': return 'status-active';
      case 'busy': return 'status-busy';
      case 'idle': return 'status-idle';
      default: return 'status-idle';
    }
  };

  const getLabelClass = (role: string) => {
    switch (role) {
      case 'coordinator': return 'label-coordinator';
      case 'executor': return 'label-executor';
      case 'observer': return 'label-observer';
      default: return 'label-coordinator';
    }
  };

  return (
    <div className="pixel-office">
      {/* Hologram effect */}
      <div className="hologram-overlay" />
      
      {/* Floating mission cards */}
      <div className="mission-card" style={{ left: '30%', top: '20%', animationDelay: '0s' }} />
      <div className="mission-card" style={{ left: '60%', top: '25%', animationDelay: '2s' }} />
      <div className="mission-card" style={{ left: '45%', top: '60%', animationDelay: '4s' }} />

      {/* Connection lines between agents */}
      {showConnections && (
        <>
          <div 
            className="connection-line" 
            style={{ 
              left: '22%', 
              top: '52%', 
              width: '28%', 
              transform: 'rotate(-5deg)'
            }} 
          />
          <div 
            className="connection-line" 
            style={{ 
              left: '52%', 
              top: '47%', 
              width: '28%', 
              transform: 'rotate(5deg)'
            }} 
          />
        </>
      )}

      {/* Agent avatars */}
      {agents.map((agent) => (
        <div key={agent.id}>
          <div 
            className={`agent-avatar ${getAgentClass(agent.role)}`}
            title={`${agent.name} - ${agent.status}`}
          >
            <div className={`status-dot ${getStatusClass(agent.status)}`} />
          </div>
          <div className={`agent-label ${getLabelClass(agent.role)}`}>
            {agent.name}
          </div>
        </div>
      ))}

      {/* Floating particles */}
      {particles.map((id, i) => (
        <div
          key={id}
          className="particle"
          style={{
            left: `${20 + (i * 8)}%`,
            animationDelay: `${i * 0.3}s`,
          }}
        />
      ))}

      {/* Terminal output */}
      <div className="pixel-terminal">
        {visibleMessages.length === 0 ? (
          <>
            <div className="terminal-line" style={{ animationDelay: '0s' }}>
              {'>'} System initialized...
            </div>
            <div className="terminal-line" style={{ animationDelay: '0.3s' }}>
              {'>'} Agents online: 3
            </div>
            <div className="terminal-line" style={{ animationDelay: '0.6s' }}>
              {'>'} Waiting for missions...
            </div>
          </>
        ) : (
          visibleMessages.map((msg, i) => (
            <div 
              key={msg.id} 
              className="terminal-line"
              style={{ animationDelay: `${i * 0.1}s` }}
            >
              {'>'} [{msg.agent}] {msg.message}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default PixelArtOffice;